import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  const values = [
    {
      icon: 'ri-shield-star-line',
      title: 'Quality Standards',
      description: 'We maintain hotel-level cleanliness and comfort in every property we manage, ensuring exceptional experiences for all guests and future tenants.'
    },
    {
      icon: 'ri-heart-3-line',
      title: 'Home-Focused Care',
      description: 'Every space we manage is treated with the care and attention it deserves, creating warm, welcoming environments that feel like home.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Professional Compliance',
      description: 'As a registered LLC, we operate with full transparency, proper documentation, and adherence to all regulations and industry standards.'
    },
    {
      icon: 'ri-team-line',
      title: 'Trusted Partnerships',
      description: 'We build lasting relationships with property owners, guests, and future tenants based on reliability, communication, and mutual respect.'
    }
  ];

  const timeline = [
    {
      year: 'Today',
      title: 'Short-Term Excellence',
      description: 'Operating successful Airbnb arbitrage and furnished short-term rentals with professional management and exceptional guest experiences.'
    },
    {
      year: 'Growing',
      title: 'Expanding Services',
      description: 'Building infrastructure, systems, and partnerships to support comprehensive residential management across multiple properties.'
    },
    {
      year: 'Future',
      title: 'Long-Term Vision',
      description: 'Launching full-scale long-term residential rental services, becoming a complete residential management solution for all housing needs.'
    }
  ];

  const faqs = [
    {
      category: "General Information",
      questions: [
        {
          question: "Where does ARM operate?",
          answer: "ARM operates throughout Pennsylvania, providing professional residential management services across the state."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Ali Residential Management</h1>
            <p className="text-xl text-teal-50 leading-relaxed">
              We're building the future of residential management, one quality home at a time. From exceptional short-term stays to comprehensive long-term solutions, ARM is your trusted partner in residential living.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Ali Residential Management was founded with a clear vision: to provide professional, high-quality residential management that property owners can trust and guests can rely on.
                </p>
                <p>
                  Starting with short-term rental operations including Airbnb arbitrage and furnished stays, we've built a reputation for exceptional service, meticulous property care, and creating comfortable, home-like experiences for every guest.
                </p>
                <p>
                  As a registered LLC, we operate with full transparency and professional standards, making us the ideal partner for landlords, business collaborators, and financial institutions. Our commitment to quality, compliance, and customer satisfaction sets us apart in the residential management industry.
                </p>
                <p>
                  Today, we're expanding our vision to include long-term residential rentals, building a comprehensive management brand that serves both short-term guests and long-term tenants with the same level of excellence and care.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/62255d568e28ab1cd04f579dcaaa4d49.jpg"
                alt="ARM Properties"
                className="rounded-xl shadow-2xl object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do, from property selection to guest services and landlord partnerships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Growth Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From short-term rental excellence to comprehensive residential management solutions
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-teal-300 via-blue-300 to-purple-300 hidden lg:block"></div>
            
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex flex-col lg:flex-row gap-8 items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className="flex-1 lg:text-right">
                    {index % 2 === 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-600 to-blue-600 text-white font-bold rounded-full mb-4">
                          {item.year}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg z-10 flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-2xl text-white"></i>
                  </div>
                  
                  <div className="flex-1">
                    {index % 2 !== 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-600 to-blue-600 text-white font-bold rounded-full mb-4">
                          {item.year}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="text-5xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent mb-4">100%</div>
              <div className="text-xl font-semibold mb-2">Quality Commitment</div>
              <p className="text-gray-300">Every property maintained to hotel-level standards</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">24/7</div>
              <div className="text-xl font-semibold mb-2">Support Available</div>
              <p className="text-gray-300">Round-the-clock assistance for all needs</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-4">LLC</div>
              <div className="text-xl font-semibold mb-2">Licensed & Compliant</div>
              <p className="text-gray-300">Registered business with full documentation</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
